import { useContext } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import HelpIcon from '@mui/icons-material/HelpOutline';
import dayjs from 'helpers/dayjs';

import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import { numberFormat } from 'helpers/i18n';
import Logo03 from 'assets/overviewSection/PS-Logo03.svg';
import BlueToolTip from 'components/shared/BlueTooltip';
import WhiteTooltip from 'components/shared/Tooltips/WhiteTooltip';

const CreditModelV2Overview = () => {
    const { statistics } = useContext(CardContext);
    const { hasAccount } = useContext(AuthContext);

    const getCreditLimit = () => {
        if (!hasAccount) {
            return numberFormat(0, 2, 2);
        }
        return numberFormat(statistics.creditLimit, 2, 2);
    };

    return (
        <Box
            display="flex"
            sx={{
                flexDirection: {
                    md: 'row',
                    xs: 'column'
                }
            }}>
            <Box
                className="statistic-card background-color-main"
                sx={{
                    backgroundImage: `url(${Logo03})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '-30% 50%'
                }}>
                <Box className="statistic-card-info">
                    <Typography className="statistic-title white-text">
                        Credit Line Limit
                        <WhiteTooltip
                            placement="top"
                            arrow
                            title={
                                <Typography className="tooltip-stat-text black-text">
                                    This is the credit limit of your business
                                </Typography>
                            }>
                            <HelpIcon sx={{ color: 'white', fontSize: '12px', marginLeft: '5px' }} />
                        </WhiteTooltip>
                    </Typography>
                    <Typography className="statistic-value white-text">{getCreditLimit()}</Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white">
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">
                        Available Balance
                        <BlueToolTip
                            placement="top"
                            arrow
                            title={
                                <Typography className="tooltip-stat-text white-text">
                                    This is the amount you can spend across all active cards
                                </Typography>
                            }>
                            <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                        </BlueToolTip>
                    </Typography>
                    <Typography className="statistic-value black-text">
                        {numberFormat(statistics.accountBalance, 2, 2)}
                    </Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white">
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">
                        Total Unallocated
                        <BlueToolTip
                            placement="top"
                            arrow
                            title={
                                <Typography className="tooltip-stat-text white-text font-weight-bold">
                                    This is the available amount you can allocate to new cards
                                </Typography>
                            }>
                            <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                        </BlueToolTip>
                    </Typography>
                    <Typography className="statistic-value black-text">
                        {numberFormat(statistics.creditLimit - statistics.allocated, 2, 2)}
                    </Typography>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white">
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">
                        Total Amount Spent
                        <BlueToolTip
                            placement="top"
                            arrow
                            title={
                                <Typography className="tooltip-stat-text white-text font-weight-bold">
                                    This is the amount you've spent during the current month
                                </Typography>
                            }>
                            <HelpIcon sx={{ color: '#617380', fontSize: '12px', marginLeft: '5px' }} />
                        </BlueToolTip>
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                        <Typography className="statistic-value black-text">
                            {numberFormat(statistics.totalAmountSpentInCurrentMonth, 2, 2)}
                        </Typography>
                        <Typography className="total-amount-spent-date" alignSelf="end">
                            {dayjs().format('MMM, YYYY')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box className="statistic-card background-color-white">
                <Box className="statistic-card-info">
                    <Typography className="statistic-title gray-text">Active cards</Typography>
                    <Typography className="statistic-value black-text">{statistics.activeCards}</Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default CreditModelV2Overview;
